import { Col, DatePicker, Form, FormInstance, Row, Input, Select, theme } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import React from 'react';
import CustomButton from 'components/custom-button';
import dayjs, { Dayjs } from 'dayjs';

export enum PAYSLIP_STATUS {
  DRAFT = 'DRAFT',
  FINAL = 'FINAL',
}

export const PAYSLIP_STATUS_LABEL: Record<string, string> = {
  [PAYSLIP_STATUS.DRAFT]: 'Nháp',
  [PAYSLIP_STATUS.FINAL]: 'Đã chốt',
};

export const PAYSLIP_STATUS_COLOR: Record<string, string> = {
  [PAYSLIP_STATUS.DRAFT]: 'default',
  [PAYSLIP_STATUS.FINAL]: 'green',
};

const { Option } = Select;

export interface PayslipFilterFormValue {
  month: Dayjs | null;
  username: string;
  status?: PAYSLIP_STATUS;
}

const initialValues: PayslipFilterFormValue = {
  month: dayjs(),
  username: '',
  status: undefined,
};

interface Props {
  /** Không gửi userId khi Tìm kiếm (người đang lọc từ URL giữ nguyên); "Xoá lọc" gửi userId: undefined. */
  onSubmit: (value: any) => void;
  form?: FormInstance<PayslipFilterFormValue>;
  /** Giá trị ban đầu lấy từ URL (?month=, hoặc để trống tháng khi lọc theo một người). */
  preset?: Partial<PayslipFilterFormValue>;
}

const Filter: React.FC<Props> = ({ onSubmit, form: formProp, preset }) => {
  const [ownForm] = Form.useForm<PayslipFilterFormValue>();
  const form = formProp || ownForm;
  const { token } = theme.useToken();

  const onClearFilter = () => {
    // Về mặc định (tháng này), không về giá trị lấy từ URL.
    form.setFieldsValue(initialValues);
    onSubmit({
      userId: undefined,
      month: dayjs().format('YYYY-MM'),
      username: '',
      status: undefined,
    });
  };

  const onFormSubmit = (value: PayslipFilterFormValue) => {
    const { month, username, status } = value;
    const monthStr = month ? dayjs(month).format('YYYY-MM') : '';
    onSubmit({ month: monthStr, username, status });
  };

  return (
    <Form
      form={form}
      name="payslips-filter"
      // Nhãn nằm trên ô nhập (như bộ lọc Tăng ca). Để mặc định "horizontal" thì .custom-form
      // xếp cột dọc nhưng ô nhập bị co còn 1px → thò xuống và bị bảng bên dưới đè lên.
      layout="vertical"
      onFinish={onFormSubmit}
      initialValues={{ ...initialValues, ...preset }}
    >
      {/* Cột nút rộng 8/24: "Tìm kiếm" + "Xoá lọc" (font Be Vietnam Pro) cần ~230px — ở 6/24
          (~216px khi màn 1280) hai nút bị xếp chồng dọc. */}
      <Row gutter={[24, 0]} className="custom-form">
        <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          <Form.Item name="username" label="TikTok ID">
            {/* size="small" giữ chiều cao 44px như hai ô bên (.custom-form ép min-height ô nhập
                bên trong; "large" + allowClear cao ~60px); cỡ chữ/lề chữ lấy theo ô "large". */}
            <Input
              placeholder="Nhập TikTok ID"
              allowClear
              size="small"
              style={{ fontSize: token.fontSizeLG, paddingInline: 11 }}
            />
          </Form.Item>
        </Col>
        <Col md={{ span: 5 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          <Form.Item name="month" label="Tháng">
            <DatePicker
              picker="month"
              placeholder="Tất cả các tháng"
              size="large"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
        <Col md={{ span: 5 }} sm={{ span: 12 }} xs={{ span: 24 }}>
          <Form.Item name="status" label="Trạng thái">
            <Select allowClear placeholder="Chọn trạng thái" size="large">
              <Option value={PAYSLIP_STATUS.DRAFT}>
                {PAYSLIP_STATUS_LABEL[PAYSLIP_STATUS.DRAFT]}
              </Option>
              <Option value={PAYSLIP_STATUS.FINAL}>
                {PAYSLIP_STATUS_LABEL[PAYSLIP_STATUS.FINAL]}
              </Option>
            </Select>
          </Form.Item>
        </Col>

        <Col md={{ span: 8 }} sm={{ span: 12 }} xs={{ span: 24 }} style={{ paddingTop: 25 }}>
          <Form.Item className="text-right mt-10">
            <CustomButton
              bgColor="secondary-green"
              htmlType="submit"
              text="Tìm kiếm"
              icon={<SearchOutlined />}
            />
            <CustomButton
              text="Xoá lọc"
              bgColor="primary-grey"
              icon={<CloseOutlined />}
              className="ml-5"
              onClick={onClearFilter}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default Filter;
